// src/core/realtime.js — wrapper sobre supabase.channel para suscripciones
// realtime con cleanup por vista.
//
// FASE 4 · F1.4.b. Cada vista (renderTribuna / renderRanking / renderGrupo /
// renderTribunaCabalas / renderTribunaPromesas) usa una `viewKey` única. Antes
// de montar nuevas suscripciones, limpia las propias con
// limpiarSuscripciones(viewKey). Si el usuario navega a otra vista sin
// re-montar la actual, los canales quedan abiertos hasta el próximo render
// (cost low para el piloto; cleanup global se evaluará en F1.4.c).
//
// IMPORTANTE — RLS y realtime:
//   Supabase Realtime aplica la RLS del usuario sobre los eventos: solo
//   recibís cambios de filas que tu policy SELECT te permite ver. Las
//   suscripciones a tablas con RLS restrictiva (usuarios, jugadores) solo
//   disparan eventos PARCIALES. Para conteos globales, usar RPC SECURITY
//   DEFINER (no confiar en el realtime para mantener el contador exacto).

import { supabase } from '../supabase.js'

const activas = new Map() // viewKey → channel[]

// Suscribe a cambios `postgres_changes` de una tabla. opts = { event, filter }.
// event: 'INSERT' | 'UPDATE' | 'DELETE' | '*' (default '*').
// filter: string del schema realtime, ej. 'group_id=eq.<uuid>'.
// callback recibe el payload del cambio.
// Devuelve el channel (raro de usar; lo normal es limpiar vía limpiarSuscripciones).
export const subscribirCambios = (viewKey, tabla, opts = {}, callback) => {
  const event = opts.event || '*'
  const filter = opts.filter || null
  const config = { event, schema: 'public', table: tabla }
  if (filter) config.filter = filter

  const channelName = `${viewKey}:${tabla}:${event}:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`
  const ch = supabase.channel(channelName)
    .on('postgres_changes', config, (payload) => {
      try { callback(payload) } catch (e) { console.error(`[realtime] ${viewKey}/${tabla} cb:`, e) }
    })
    .subscribe()

  if (!activas.has(viewKey)) activas.set(viewKey, [])
  activas.get(viewKey).push(ch)
  return ch
}

// Limpia todas las suscripciones de una vista (las desconecta del server +
// remueve del registro local). Llamar al inicio del render para no acumular.
export const limpiarSuscripciones = (viewKey) => {
  const list = activas.get(viewKey)
  if (!list) return
  for (const ch of list) {
    try { supabase.removeChannel(ch) } catch (e) { /* swallow */ }
  }
  activas.delete(viewKey)
}

// Limpia TODAS las suscripciones de todas las vistas. Útil para tests y para
// un cleanup global futuro (por ejemplo, al logout).
export const limpiarTodasLasSuscripciones = () => {
  for (const viewKey of activas.keys()) limpiarSuscripciones(viewKey)
}

// Cantidad de canales activos por viewKey. Útil para tests + debug.
export const _getSuscripcionesActivas = () => {
  const out = {}
  for (const [k, v] of activas.entries()) out[k] = v.length
  return out
}
